// Recording what you just did, then handing it over in a form an agent can act on.
//
// THE POINT IS THE CONTEXT, NOT THE FOOTAGE. "This button isn't working" costs
// a round trip to answer and often several; the same sentence with the route,
// the control, the build and the console at that moment is a fix. So the
// recording collects those while you click, and the only thing you write is
// what you wanted to happen instead.
//
// It is off until you start it, it never records what you type into a field,
// and nothing leaves the browser: the report is copied to your clipboard or
// saved to your machine, and you decide where it goes. Storing and sharing
// these is a later step, deliberately not in this first cut.

import { useCallback, useEffect, useRef, useState } from "react";
import { Check, Circle, Copy, Download, History, MessageSquare, Send, Square, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { capturesApi } from "@/lib/bloxxData";
import { useScope } from "@/lib/scope";
import { Textarea } from "@/components/ui/textarea";
import { Callout } from "@/components/ui/status";
import {
  BUFFER_SECONDS, FEEDBACK_UI_ATTR, asMarkdown, startBuffer, startCapture,
  type BufferHandle, type CaptureHandle, type FeedbackReport, type FeedbackStep,
} from "@/lib/feedback/capture";

// Everything the recorder draws carries this, so the capture skips its own UI.
const ours = { [FEEDBACK_UI_ATTR]: "" };

/**
 * THE BUTTON MUST NEVER SIT ON TOP OF WHAT IT FLOATS OVER (plan rev 7, B5). While it
 * shows, it publishes how much of the bottom edge it takes as `--dock-bottom` on
 * <html>; the layout's <main> pads by that much, and on a phone the scroll area
 * stops above it, so no line of text or control ever ends up underneath. The
 * offset includes the safe-area inset, so the home indicator is allowed for too.
 */
const DOCK_BUTTON = "calc(44px + 16px + env(safe-area-inset-bottom, 0px))";
// --nav-bottom: the phone's bottom section bar (nav v2, components/shell), which the button floats above.
// --actions-lift: a screen's own bottom action bar on a phone (Review's Approve / Request changes), set in shell.css.
const BUTTON_BOTTOM = "calc(var(--nav-bottom, 0px) + var(--actions-lift, 0px) + 16px + env(safe-area-inset-bottom, 0px))";

/**
 * True while a touch device's on-screen keyboard is up: a text field has focus and
 * the visual viewport is more than 150 px shorter than the tallest it has been at
 * this width. The button hides then, so it can't cover the field being typed in.
 */
function useKeyboardOpen(): boolean {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const vv = window.visualViewport;
    const touch = (navigator.maxTouchPoints ?? 0) > 0 || window.matchMedia?.("(pointer: coarse)").matches;
    if (!vv || !touch) return;
    let tallest = vv.height;
    let width = window.innerWidth;
    const typing = () => {
      const el = document.activeElement as HTMLElement | null;
      if (!el) return false;
      if (el.isContentEditable || el.tagName === "TEXTAREA") return true;
      return el.tagName === "INPUT" && !["checkbox", "radio", "button", "submit", "reset", "range", "color", "file"].includes((el as HTMLInputElement).type);
    };
    const check = () => {
      if (window.innerWidth !== width) { width = window.innerWidth; tallest = vv.height; }
      if (!typing()) tallest = Math.max(tallest, vv.height);
      setOpen(typing() && tallest - vv.height > 150);
    };
    vv.addEventListener("resize", check);
    document.addEventListener("focusin", check);
    document.addEventListener("focusout", check);
    return () => {
      vv.removeEventListener("resize", check);
      document.removeEventListener("focusin", check);
      document.removeEventListener("focusout", check);
    };
  }, []);
  return open;
}

type Phase =
  | { kind: "idle" }
  | { kind: "starting" }
  | { kind: "recording"; handle: CaptureHandle; steps: FeedbackStep[] }
  | { kind: "review"; report: FeedbackReport };

const describe = (step: FeedbackStep) => {
  if (step.kind === "click") return `Clicked "${step.target?.name}"`;
  if (step.kind === "field") return `${step.detail} "${step.target?.name}"`;
  if (step.kind === "route") return `Went to ${step.route}`;
  if (step.kind === "error") return `Error: ${step.detail}`;
  return `Request failed: ${step.detail}`;
};

export function FeedbackRecorder() {
  const { current, workspaces } = useScope();
  const target = current?.id ?? workspaces[0]?.id ?? "";
  const [sending, setSending] = useState<"idle" | "sending" | "sent" | "failed">("idle");
  const [sendError, setSendError] = useState("");
  const [open, setOpen] = useState(false);
  const [phase, setPhase] = useState<Phase>({ kind: "idle" });
  const [note, setNote] = useState("");
  const [copied, setCopied] = useState(false);
  const [trouble, setTrouble] = useState(0);
  const phaseRef = useRef(phase);
  phaseRef.current = phase;
  const keyboard = useKeyboardOpen();
  const recordingBar = useRef<HTMLDivElement>(null);

  // --dock-bottom: what the layout leaves free at the bottom (see DOCK_BUTTON).
  const dock = phase.kind === "recording" ? "bar" : !open && !keyboard ? "button" : "none";
  useEffect(() => {
    const root = document.documentElement;
    if (dock === "button") root.style.setProperty("--dock-bottom", DOCK_BUTTON);
    else if (dock === "none") root.style.setProperty("--dock-bottom", "0px");
    let observer: ResizeObserver | undefined;
    if (dock === "bar" && recordingBar.current) {
      const bar = recordingBar.current;
      const set = () => root.style.setProperty("--dock-bottom", `${Math.ceil(bar.getBoundingClientRect().height)}px`);
      set();
      observer = typeof ResizeObserver === "function" ? new ResizeObserver(set) : undefined;
      observer?.observe(bar);
    }
    return () => { observer?.disconnect(); root.style.removeProperty("--dock-bottom"); };
  }, [dock]);
  const buffer = useRef<BufferHandle | null>(null);

  /**
   * THE BUFFER RUNS WHENEVER A DELIBERATE RECORDING IS NOT, and never beside
   * one: both patch `fetch` and `console.error`, so two sets of listeners would
   * log every failure twice and unpatching would restore a patched function.
   *
   * ONE EFFECT OWNS IT, START TO FINISH. It was two, a starter keyed on the
   * phase and a separate unmount cleanup, and they raced: the second one's
   * cleanup stopped the buffer while the first still held the handle, saw it
   * as present and declined to make another. The listeners were detached and
   * the recorder went on reporting a buffer it no longer had. React's double
   * invoke in development is what made it happen every time rather than
   * occasionally, which is the whole point of it.
   */
  useEffect(() => {
    if (phase.kind === "recording" || phase.kind === "starting") return;
    const handle = startBuffer();
    buffer.current = handle;
    setTrouble(0);
    const tick = setInterval(() => setTrouble(handle.trouble()), 2000);
    return () => {
      clearInterval(tick);
      handle.stop();
      if (buffer.current === handle) buffer.current = null;
    };
  }, [phase.kind]);

  const begin = useCallback(async (stills: boolean) => {
    setPhase({ kind: "starting" });
    try {
      // The picker has to be reached from the click that opened it, so nothing
      // is awaited before startCapture asks for it.
      const handle = await startCapture({
        stills,
        onStep: (steps) =>
          setPhase((p) => (p.kind === "recording" ? { ...p, steps } : p)),
      });
      setPhase({ kind: "recording", handle, steps: [] });
    } catch {
      setPhase({ kind: "idle" });
    }
  }, []);

  const [retro, setRetro] = useState(false);
  const keepLastMinute = useCallback(() => {
    const report = buffer.current?.take();
    if (!report) return;
    setRetro(true);
    setPhase({ kind: "review", report });
  }, []);

  const finish = useCallback(() => {
    const current = phaseRef.current;
    if (current.kind !== "recording") return;
    setRetro(false);
    setPhase({ kind: "review", report: current.handle.stop() });
  }, []);

  // Stop cleanly if the page is closed mid-recording, so the patched fetch and
  // history methods are always put back.
  useEffect(() => {
    return () => {
      const current = phaseRef.current;
      if (current.kind === "recording") current.handle.stop();
    };
  }, []);

  // Ctrl/Cmd + Shift + F, which no browser claims and no field swallows.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && e.shiftKey && e.key.toLowerCase() === "f") {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const report = phase.kind === "review" ? { ...phase.report, note } : null;

  const copy = async () => {
    if (!report) return;
    await navigator.clipboard.writeText(asMarkdown(report));
    setCopied(true);
    setTimeout(() => setCopied(false), 1600);
  };

  const download = () => {
    if (!report) return;
    const blob = new Blob([JSON.stringify(report, null, 2)], { type: "application/json" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `bloxx-feedback-${new Date(report.startedAt).toISOString().replace(/[:.]/g, "-")}.json`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const reset = () => {
    setPhase({ kind: "idle" });
    setNote("");
    setSending("idle");
    setSendError("");
  };

  /**
   * Send it, so somebody else can read it.
   *
   * THE STILLS GO SEPARATELY, as the JPEGs they are. Putting a dozen base64
   * screenshots in the JSON body would make a request nobody should send and a
   * row nobody should store, so the steps say which of them HAS a picture and
   * the pictures follow one at a time. A still that fails to upload costs its
   * picture and not the report.
   */
  const send = async () => {
    if (!report || !target) return;
    setSending("sending");
    setSendError("");
    try {
      const made = await capturesApi.create(target, {
        kind: "feedback",
        title: report.note.trim().slice(0, 80) || `Feedback on ${new URL(report.where.href).pathname}`,
        note: report.note,
        where: report.where,
        retrospective: retro,
        // The bytes stay here; the step carries only the still's NAME.
        steps: report.steps.map(({ shot, shotId, ...rest }) => ({ ...rest, ...(shot && shotId ? { shot: shotId } : {}) })),
      });
      for (const step of report.steps) {
        if (!step.shot || !step.shotId) continue;
        const bytes = Uint8Array.from(atob(step.shot), (c) => c.charCodeAt(0));
        await capturesApi
          .shot(target, made.capture.id, step.shotId, new Blob([bytes], { type: "image/jpeg" }))
          .catch(() => undefined);
      }
      setSending("sent");
    } catch (e) {
      setSending("failed");
      setSendError((e as Error)?.message ?? String(e));
    }
  };

  // While recording, the panel gets out of the way: it is a bar, not a dialog,
  // because you have to be able to use the app underneath it.
  if (phase.kind === "recording") {
    return (
      <div {...ours} ref={recordingBar} className="fixed inset-x-0 bottom-0 z-50 flex flex-wrap items-center gap-3 border-t bg-background px-4 py-3 shadow-lg" style={{ paddingBottom: "calc(12px + env(safe-area-inset-bottom, 0px))" }}>
        <span className="flex items-center gap-2 text-sm font-medium">
          {/* A solid mark rather than a pulsing one. The pulse utility is
              reserved for loading placeholders here, and the build check that
              enforces that reads the file as text, so naming it even in a
              comment fails the build. */}
          <Circle className="h-3 w-3 fill-destructive text-destructive" aria-hidden />
          Recording
        </span>
        <span className="text-sm text-muted-foreground">
          {phase.steps.length} step{phase.steps.length === 1 ? "" : "s"}
          {phase.handle.hasStills() ? " with screenshots" : ", no screenshots"}
        </span>
        <span className="min-w-0 flex-1 truncate text-sm text-muted-foreground">
          {phase.steps.length ? describe(phase.steps[phase.steps.length - 1]!) : "Use the app as you normally would."}
        </span>
        <Button size="sm" onClick={finish}>
          <Square className="mr-1.5 h-3.5 w-3.5" />
          Stop and review
        </Button>
      </div>
    );
  }

  if (!open) {
    if (keyboard) return null;
    return (
      <button
        {...ours}
        type="button"
        onClick={() => setOpen(true)}
        title="Record feedback (Ctrl+Shift+F)"
        data-feedback-button=""
        style={{ bottom: BUTTON_BOTTOM }}
        className="fixed right-4 z-40 flex h-11 w-11 items-center justify-center rounded-full border bg-background text-muted-foreground shadow-md transition-colors hover:text-foreground"
      >
        <MessageSquare className="h-5 w-5" />
        {/* Something has just failed and is still inside the buffer. The mark is
            the whole reason the buffer is worth having: it tells you there is
            something to keep at the moment you could still keep it. */}
        {trouble > 0 && (
          <span className="absolute -right-0.5 -top-0.5 h-3 w-3 rounded-full border-2 border-background bg-destructive" aria-hidden />
        )}
        <span className="sr-only">
          Record feedback{trouble > 0 ? `, ${trouble} recent error${trouble === 1 ? "" : "s"} can still be captured` : ""}
        </span>
      </button>
    );
  }

  return (
    <div {...ours} className="fixed bottom-4 right-4 z-50 w-[min(28rem,calc(100vw-2rem))] rounded-lg border bg-background p-5 shadow-xl">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-sm font-semibold">Record feedback</h2>
        <button type="button" onClick={() => { setOpen(false); reset(); }} className="text-muted-foreground hover:text-foreground">
          <X className="h-4 w-4" />
          <span className="sr-only">Close</span>
        </button>
      </div>

      {phase.kind === "idle" && (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            Either way it keeps the page you were on, the control you clicked, the build you were running and anything the
            console or network reported, then you say what you wanted instead. It never records what you type into a field.
          </p>
          {/* THE RETROSPECTIVE ONE IS FIRST, because it is the one that matches
              how you actually notice things: after they have happened. */}
          <div className="mt-4 rounded-md border p-3">
            <div className="flex items-center justify-between gap-3">
              <div>
                <p className="text-sm font-medium">Something just went wrong</p>
                <p className="text-xs text-muted-foreground">
                  Keep the last {BUFFER_SECONDS} seconds, without having to make it happen again.
                </p>
              </div>
              <Button size="sm" variant={trouble > 0 ? "default" : "outline"} onClick={keepLastMinute}>
                <History className="mr-1.5 h-3.5 w-3.5" />
                Capture it
              </Button>
            </div>
            {trouble > 0 && (
              <p className="mt-2 text-xs text-destructive">
                {trouble} error{trouble === 1 ? "" : "s"} caught in that window.
              </p>
            )}
          </div>

          <p className="mt-4 text-sm font-medium">Or show me what you mean</p>
          <div className="mt-2 flex flex-wrap gap-2">
            <Button size="sm" onClick={() => void begin(true)}>
              <Circle className="mr-1.5 h-3.5 w-3.5 fill-current" />
              Record with screenshots
            </Button>
            <Button size="sm" variant="outline" onClick={() => void begin(false)}>
              Record without
            </Button>
          </div>
          <p className="mt-3 text-xs text-muted-foreground">
            Screenshots need you to share this tab, in Chrome or Edge. The last {BUFFER_SECONDS} seconds are held in this
            tab and nowhere else, and nothing leaves the browser until you send it.
          </p>
        </>
      )}

      {phase.kind === "starting" && <p className="mt-3 text-sm text-muted-foreground">Waiting for you to choose what to share…</p>}

      {phase.kind === "review" && report && (
        <>
          <p className="mt-2 text-sm text-muted-foreground">
            {report.steps.length} step{report.steps.length === 1 ? "" : "s"} recorded
            {report.steps.filter((s) => s.shot).length ? `, ${report.steps.filter((s) => s.shot).length} with a screenshot` : ""}.
          </p>
          {report.steps.some((s) => s.kind === "error" || s.kind === "request") && (
            <Callout tone="warning" className="mt-3">
              {report.steps.filter((s) => s.kind === "error" || s.kind === "request").length} error
              {report.steps.filter((s) => s.kind === "error" || s.kind === "request").length === 1 ? " was" : "s were"} caught while you recorded.
            </Callout>
          )}
          <label className="mt-3 block text-sm font-medium" htmlFor="feedback-note">
            What did you want to happen?
          </label>
          <Textarea
            id="feedback-note"
            className="mt-1.5"
            rows={3}
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="When I click Save here I want it to keep the filter I picked."
          />
          <div className="mt-3 max-h-40 overflow-y-auto rounded border bg-secondary/30 p-2 text-xs">
            <ol className="space-y-1">
              {report.steps.map((step, i) => (
                <li key={i} className={step.kind === "error" || step.kind === "request" ? "text-destructive" : "text-muted-foreground"}>
                  {describe(step)}
                </li>
              ))}
            </ol>
          </div>
          <div className="mt-4 flex flex-wrap gap-2">
            {target && (
              <Button size="sm" disabled={sending === "sending" || sending === "sent"} onClick={() => void send()}>
                {sending === "sent" ? <Check className="mr-1.5 h-3.5 w-3.5" /> : <Send className="mr-1.5 h-3.5 w-3.5" />}
                {sending === "sent" ? "Sent" : sending === "sending" ? "Sending" : "Send it"}
              </Button>
            )}
            <Button size="sm" variant={target ? "outline" : "default"} onClick={() => void copy()}>
              <Copy className="mr-1.5 h-3.5 w-3.5" />
              {copied ? "Copied" : "Copy as text"}
            </Button>
            <Button size="sm" variant="outline" onClick={download}>
              <Download className="mr-1.5 h-3.5 w-3.5" />
              Save a copy
            </Button>
            <Button size="sm" variant="ghost" onClick={reset}>
              Start again
            </Button>
          </div>
          {sending === "failed" && (
            <Callout tone="danger" className="mt-3">
              That did not send. {sendError} It is still here, and Save a copy keeps it.
            </Callout>
          )}
          {sending === "sent" && (
            <p className="mt-3 text-xs text-muted-foreground">
              Kept in {workspaces.find((w) => w.id === target)?.name ?? "your workspace"}, with its screenshots.
            </p>
          )}
        </>
      )}
    </div>
  );
}
